import {
  AppInstanceShort,
  AppInstanceStatus,
  AttachmentMetadata,
  AttachmentMetadataResponse,
  CustomContextField,
  MayBeNull,
  ProjectContext,
  AppInstanceVersionType,
  AppInstanceAppearances,
} from '@wpp-open/core'

import {
  CodeUploadAppConfig,
  DevHubAppsType,
  EmbeddedCodeAppConfig,
  EmbeddedLinkConfig,
  ExternalLinkConfig,
  MiroBoardAppConfig,
  PageBuilderConfig,
} from 'types/apps/devHubApps'
import { PageBuilderContent } from 'types/apps/leanApps'

export type AppInstanceContext =
  | { embeddedCode?: string }
  | { url?: string }
  | { content?: PageBuilderContent }
  | ProjectContext
  | {}

export interface DefaultTemplate {
  id: string
  name: string
  description: string
  groups?: {
    groups?: {
      id: string
      order: number
    }[]
    subgroups?: {
      id: string
      order: number
    }[]
  }
  appliedOnHierarchies?: {
    assignmentId: string
    assignmentType: string
  }[]
}

export interface AppInstanceTemplate {
  id: string
  description: MayBeNull<string>
  devhubAppId: string
  devhubAppVersionId: string
  osRoute?: string
  context?: AppInstanceContext
  group: MayBeNull<{
    id: string
    name: string
    description: MayBeNull<string>
    order: MayBeNull<number>
  }>
  subgroup: MayBeNull<{
    id: string
    name: string
    description: MayBeNull<string>
    order: MayBeNull<number>
  }>
  logoThumbnailTempFile?: MayBeNull<File>
  logoThumbnailTemp?: MayBeNull<string>
  appType?: DevHubAppsType
  position: number
  logoOriginal?: MayBeNull<AttachmentMetadataResponse>
  logoThumbnail?: MayBeNull<AttachmentMetadataResponse>
  customContext: CustomContextField[]
  routePath: MayBeNull<string>
  name: string
  shortDescription: MayBeNull<string>
  logoMeta: MayBeNull<{
    original: AttachmentMetadata
    thumbnail: AttachmentMetadata
  }>
  isTemplate: boolean
  tenantId?: string
  ownerName: MayBeNull<string>
  ownerEmail: MayBeNull<string>
  versionType: AppInstanceVersionType
  status?: AppInstanceStatus
  appearances: MayBeNull<AppInstanceAppearances>
  flags: {
    visible: boolean
    accessRequestable: boolean
  }
  config?: MayBeNull<
    | ExternalLinkConfig
    | EmbeddedCodeAppConfig
    | EmbeddedLinkConfig
    | MiroBoardAppConfig
    | CodeUploadAppConfig
    | PageBuilderConfig
  >
  action: AppInstanceTemplateAction
}

export interface TemplateResponse extends DefaultTemplate {
  appInstances: AppInstanceShort[]
}

export interface Template extends DefaultTemplate {
  appInstances: AppInstanceTemplate[]
}

export interface TemplateAppInstanceGroups {
  groups?: {
    id: string
    order: number
  }[]
  subgroups?: {
    id: string
    groupId?: string
    order: number
  }[]
}

export interface AppInstanceEditTemplateRequest {
  osRoute?: string
  name: string
  description: MayBeNull<string>
  status?: string
  context?: AppInstanceContext
  groupId: MayBeNull<string>
  subgroupId: MayBeNull<string>
  logoThumbnail: MayBeNull<{
    key: string
    name: string
    size: number
  }>
  logoOriginal: MayBeNull<{
    key: string
    name: string
    size: number
  }>
  flags: {
    visible: boolean
    accessRequestable: boolean
  }
  id?: string
  action: AppInstanceTemplateAction.EDIT
  position?: number
}

export interface AppInstanceRemoveTemplateRequest {
  id: string
  action: AppInstanceTemplateAction.REMOVE
}

export interface AppInstanceNewTemplateRequest {
  devhubAppId: string
  devhubAppVersionId: string
  osRoute?: string
  name: string
  description: MayBeNull<string>
  status?: string
  context?: AppInstanceContext
  groupId: MayBeNull<string>
  subgroupId: MayBeNull<string>
  logoThumbnail: MayBeNull<{
    key: string
    name: string
    size: number
  }>
  logoOriginal: MayBeNull<{
    key: string
    name: string
    size: number
  }>
  flags: {
    visible: boolean
    accessRequestable: boolean
  }
  action: AppInstanceTemplateAction.CREATE_NEW
  position?: number
}

export interface AppInstanceExistedTemplateRequest {
  id: string
  groupId: MayBeNull<string>
  subgroupId: MayBeNull<string>
  action: AppInstanceTemplateAction.CREATE_EXISTING
}

export type AppInstanceTemplateRequest =
  | AppInstanceRemoveTemplateRequest
  | AppInstanceEditTemplateRequest
  | AppInstanceNewTemplateRequest
  | AppInstanceExistedTemplateRequest

export enum AppInstanceTemplateAction {
  REMOVE = 'remove',
  CREATE_NEW = 'create_new',
  CREATE_EXISTING = 'create_existing',
  EDIT = 'edit',
  EXIST = 'exist',
}
